import React, { useEffect, useState } from 'react';
import { scoringAPI, assessmentsAPI } from '../services/api';
import { Assessment, ScoringResult, GapItem } from '../types';
import { ScoreGauge } from '../components/dashboard/ScoreGauge';
import { MaturityBadge } from '../components/dashboard/MaturityBadge';
import { CategoryHeatmap } from '../components/dashboard/CategoryHeatmap';

const MaturityPage: React.FC = () => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selected, setSelected] = useState<Assessment | null>(null);
  const [scoring, setScoring] = useState<ScoringResult | null>(null);
  const [gaps, setGaps] = useState<GapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingScoring, setLoadingScoring] = useState(false);

  useEffect(() => {
    assessmentsAPI.list().then((res) => {
      const submitted = res.data.filter((a: Assessment) => a.status !== 'draft');
      setAssessments(submitted);
      if (submitted.length > 0) {
        loadScoring(submitted[0]);
      }
      setLoading(false);
    });
  }, []);

  const loadScoring = async (a: Assessment) => {
    setSelected(a);
    setLoadingScoring(true);
    try {
      const [sRes, gRes] = await Promise.all([
        scoringAPI.breakdown(a.id),
        scoringAPI.gaps(a.id),
      ]);
      setScoring(sRes.data);
      setGaps(gRes.data);
    } catch (e) {
      console.error('Scoring load error', e);
    } finally {
      setLoadingScoring(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Maturity Assessment</h1>

      {/* Assessment selector */}
      {assessments.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {assessments.map((a) => (
            <button
              key={a.id}
              onClick={() => loadScoring(a)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                selected?.id === a.id ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:border-blue-400'
              }`}
            >
              Assessment #{a.id}
              {a.submitted_at && <span className="ml-1 text-xs opacity-70">{new Date(a.submitted_at).toLocaleDateString()}</span>}
            </button>
          ))}
        </div>
      )}

      {assessments.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <span className="text-5xl">📋</span>
          <p className="mt-3">No completed assessments yet. Complete an assessment to see maturity data.</p>
        </div>
      )}

      {scoring && !loadingScoring && (
        <>
          {/* Score + maturity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center">
              <p className="text-sm font-semibold text-gray-600 mb-3">Overall Score</p>
              <ScoreGauge score={scoring.overall_score} size={160} />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center gap-3">
              <p className="text-sm font-semibold text-gray-600">Maturity Level</p>
              <MaturityBadge level={scoring.maturity_level} label={scoring.maturity_label} large />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <p className="text-sm font-semibold text-gray-600 mb-3">Score Breakdown</p>
              {scoring.breakdown.map((b) => (
                <div key={b.category_id} className="flex justify-between text-xs py-1 border-b border-gray-50">
                  <span className="text-gray-600 truncate">{b.category_name}</span>
                  <span className="font-semibold text-gray-800">{b.score_percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Heatmap */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Category Compliance Heatmap</h2>
            <CategoryHeatmap
              categories={scoring.breakdown.map((b) => ({
                category_id: b.category_id,
                category_name: b.category_name,
                score_percentage: b.score_percentage,
                weight: b.weight,
                status: b.score_percentage >= 80 ? 'excellent'
                  : b.score_percentage >= 60 ? 'good'
                  : b.score_percentage >= 40 ? 'needs_improvement'
                  : 'critical',
              }))}
            />
          </div>

          {/* Gaps table */}
          {gaps.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="text-sm font-semibold text-gray-800 mb-4">Compliance Gaps ({gaps.length})</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 pr-4 text-gray-500 font-semibold">Requirement</th>
                      <th className="text-left py-2 pr-4 text-gray-500 font-semibold">Category</th>
                      <th className="text-left py-2 pr-4 text-gray-500 font-semibold">Priority</th>
                      <th className="text-left py-2 pr-4 text-gray-500 font-semibold">Gap</th>
                      <th className="text-left py-2 text-gray-500 font-semibold">Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.map((g) => (
                      <tr key={g.question_id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 pr-4 max-w-xs">
                          <p className="text-gray-800 line-clamp-2">{g.question_text}</p>
                          <span className={`mt-0.5 inline-block text-[10px] px-1.5 py-0.5 rounded ${
                            g.requirement_type === 'must_have' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {g.requirement_type === 'must_have' ? 'Must Have' : 'Good to Have'}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{g.category_name}</td>
                        <td className="py-2 pr-4">
                          <span className={`px-2 py-0.5 rounded font-bold ${
                            g.priority === 'HIGH' ? 'bg-red-100 text-red-700'
                            : g.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-600'
                          }`}>{g.priority}</span>
                        </td>
                        <td className="py-2 pr-4 text-gray-700 font-medium">{g.gap_score.toFixed(1)}</td>
                        <td className="py-2">
                          {g.evidence_required && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              g.evidence_uploaded ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                            }`}>
                              {g.evidence_uploaded ? '✓ Uploaded' : '✗ Missing'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MaturityPage;
